import { lazy, Suspense, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import review1 from "../../../images/review-1.png";
import review2 from "../../../images/review-2.png";
import review3 from "../../../images/review-3.png";
import review4 from "../../../images/review-4.png";
import review5 from "../../../images/review-5.png";
import TrialEmailForm from '../../../forms/OnboardingForm/TrialEmailForm';
import LiveTicker from '../../../components/LiveTicker';
import { apiService } from '../../../services/apiService';
import CorporateHeader from './CorporateHeader';
import SeoHelmet from '../../../templates/SeoHelmet';
import MarketPredictedCard from '../../../templates/MarketPredictedCard';
import CardInform from '../../../templates/CardInform';
import CompetitionFilter, { ALL_COMPETITIONS, sortCompetitions } from '../../../helpers/CompetitionFilter';
import HelperFunctions from '../../../helpers/HelperFunctions';
import { getFixtureDateRange, toUTCDate } from '../../../helpers/dateUtils';
import { TickerSkeleton, CompetitionFilterSkeleton, MarketPredictedCardSkeleton } from '../../../templates/LandingFixtureSkeletons';



// The hero (LCP element) above is statically imported with this page so it renders
// with the entry bundle — no second chunk round-trip for LCP. Everything below the
// fold (features, stats, predictions images, testimonials, pricing, FAQ, CTA,
// footer) is code-split into its own chunk so it doesn't bloat the entry bundle
// and hurt FCP/TBT on every route.
const LandingBelowFold = lazy(() => import('./LandingBelowFold'));


const RelatedIntelligence = lazy(() => import('../../../components/RelatedIntelligence'));
const PlayerNews = lazy(() => import('../../../components/PlayerNews'));
const MatchNews = lazy(() => import('../../../components/MatchNews'));

// How many "today's picks" cards the hero shows for the selected league.
const HERO_CARD_COUNT = 2;
// The ticker is a marquee, not a fixture list: it takes the next few fixtures per
// league, interleaved, so every league is visible within a single scroll pass
// without rendering the whole feed twice (LiveTicker duplicates the list to loop).
const TICKER_FIXTURES_PER_COMPETITION = 4;
const TICKER_FIXTURE_LIMIT = 15;
// Enough of the window to fill the ticker for every league. The limit is global and
// chronological, not per-league, so it has to be comfortably above
// TICKER_FIXTURES_PER_COMPETITION × number of leagues.


const LoggedOutHomePlusTwo = () => {
    const navigate = useNavigate();
    const [fixtures, setFixtures] = useState([]);
    // Drives the ticker/competition-filter skeletons below until the fixture-landing-list
    // request resolves (success or error) — separate from `fixtures` being empty, which
    // can also legitimately mean "loaded, nothing upcoming".
    const [fixturesLoading, setFixturesLoading] = useState(true);
    // null until the feed lands and the default league is resolved below.
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const hasFetched = useRef(false);
    const didInitCompetition = useRef(false);
    const { competitionDisplayName, slugifyForUrl } = HelperFunctions();

    // One unfiltered call, same as the fixtures-preview page: the backend owns the
    // featured-league set (competition_ids), so every league comes back together and
    // there's nothing to merge client-side. Replaces the old pair of
    // single-competition next-round-fixtures + upcoming calls.
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const now = new Date();

        const dateFrom = toUTCDate(now).toISOString().slice(0, 10);

        const { dateTo } = getFixtureDateRange();

        apiService
            .apiGet(`${import.meta.env.VITE_API_URL}/fixture-landing-list?date_from=${dateFrom}&date_to=${dateTo}`)
            .then(
                (data) => { setFixtures(Array.isArray(data?.fixtures) ? data.fixtures : []); setFixturesLoading(false); },
                (error) => { console.error('Failed to load landing fixtures', error); setFixturesLoading(false); }
            );
    }, []);

    const avatars = [review1, review2, review3, review4, review5];

    const upcomingFixtures = useMemo(() => (
        fixtures
            .filter((fixture) => fixture?.status !== 'finished'
                && fixture?.match_date
                && new Date(fixture.match_date).getTime() > Date.now())
            .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
    ), [fixtures]);

    // Only leagues that still have an upcoming fixture get a chip, so a chip can never
    // select an empty set. sortCompetitions applies the shared chip order (PL first).
    const competitions = useMemo(() => {
        const map = new Map();
        upcomingFixtures.forEach((fixture) => {
            const comp = fixture.competition;
            if (comp?.id != null && !map.has(comp.id)) {
                map.set(comp.id, { id: comp.id, season_name: competitionDisplayName(comp) });
            }
        });
        return sortCompetitions(Array.from(map.values()));
        // competitionDisplayName comes from a helper factory recreated each render;
        // including it would defeat this memo. Intentionally keyed on fixtures only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upcomingFixtures]);

    // Land on the Premier League when it's available, else the first league in chip
    // order. Runs once so it never overrides a league the visitor picked.
    useEffect(() => {
        if (didInitCompetition.current || competitions.length === 0) return;
        didInitCompetition.current = true;

        const premierLeague = competitions.find((comp) => comp.season_name === 'Premier League');
        setSelectedCompetition(String((premierLeague || competitions[0]).id));
    }, [competitions]);

    // Not every fixture has a prediction yet (mostly the leagues furthest from kickoff),
    // and a "N markets predicted" card reading "—" undersells the product — so the cards
    // and the CTA lead with predicted fixtures, then fall back to chronological order for
    // a league that has none yet.
    const competitionFixtures = useMemo(() => {
        const scoped = !selectedCompetition || selectedCompetition === ALL_COMPETITIONS
            ? upcomingFixtures
            : upcomingFixtures.filter((fixture) => String(fixture.competition?.id) === String(selectedCompetition));

        const isPredicted = (fixture) => fixture?.prediction?.home_win_pct != null;
        return [...scoped.filter(isPredicted), ...scoped.filter((fixture) => !isPredicted(fixture))];
    }, [upcomingFixtures, selectedCompetition]);

    const upcomingFixturePicks = useMemo(
        () => competitionFixtures.slice(0, HERO_CARD_COUNT),
        [competitionFixtures]
    );

    // The fixture the news sections below the hero follow: the same one leading
    // the cards, so the page stays on one match and follows the league filter.
    const newsFixture = competitionFixtures[0] ?? null;
    const newsFixtureId = newsFixture?.fixture_id ?? null;

    const [newsFixtureDetail, setNewsFixtureDetail] = useState(null);
    useEffect(() => {
        if (!newsFixtureId) { setNewsFixtureDetail(null); return undefined; }
        let cancelled = false;
        apiService
            .apiGet(import.meta.env.VITE_API_URL + '/fixture-landing/' + newsFixtureId)
            .then(
                (data) => { if (!cancelled) setNewsFixtureDetail(data?.fixture ?? null); },
                () => { if (!cancelled) setNewsFixtureDetail(null); }
            );
        return () => { cancelled = true; };
    }, [newsFixtureId]);

    // The ticker follows the league filter: pick a league and it narrows to that
    // league's fixtures, chronologically. Before the default resolves (or on "All")
    // it interleaves across leagues so every one shows up in a single scroll pass.
    const tickerFixtures = useMemo(() => {
        const scopedCompetitions = selectedCompetition && selectedCompetition !== ALL_COMPETITIONS
            ? competitions.filter((comp) => String(comp.id) === String(selectedCompetition))
            : competitions;

        // A league on its own gets the whole marquee; when leagues share it they're
        // capped so the next fixture of each one lands in the first scroll pass.
        const perCompetition = scopedCompetitions.length > 1
            ? TICKER_FIXTURES_PER_COMPETITION
            : TICKER_FIXTURE_LIMIT;

        const queues = scopedCompetitions.map((comp) => (
            upcomingFixtures
                .filter((fixture) => fixture.competition?.id === comp.id)
                .slice(0, perCompetition)
        ));

        const interleaved = [];
        for (let slot = 0; slot < perCompetition; slot += 1) {
            queues.forEach((queue) => {
                if (queue[slot]) interleaved.push(queue[slot]);
            });
        }
        return interleaved.slice(0, TICKER_FIXTURE_LIMIT);
    }, [competitions, upcomingFixtures, selectedCompetition]);

    // The lead fixture drives the badge label and both "see the predictions" links, so
    // the CTA always lands on the same match the first card shows.
    const leadFixture = competitionFixtures[0];

    const fixturesPreviewHref = leadFixture
        ? `/fixtures-preview/${leadFixture.fixture_id}/${slugifyForUrl(`${leadFixture.home_team?.name || ''} vs ${leadFixture.away_team?.name || ''}`)}`
        : '/fixtures-preview';

    // Leagues label their stage differently (matchweek / group / knockout round), and
    // some send none at all — then the league's own name carries the badge.
    const heroBadgeLabel = useMemo(() => {
        const stage = leadFixture?.stage;
        if (stage?.type === 'knockout' && stage?.name) return stage.name;
        if (stage?.group) return stage.group;
        if (stage?.round) return `MW ${stage.round}`;
        return competitionDisplayName(leadFixture?.competition) || 'Football';
        // Same helper-factory caveat as `competitions` above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadFixture]);

    return (
        <div className="sk-corporate">
            <SeoHelmet
                title="AI Football Predictions, News & Insights | SportsKinetic"
                description="SportsKinetic delivers AI-powered football predictions, match previews, player insights, transfer news and fixture analysis, all in one smart platform."
                slug="/"
                image="/assets/facebook-og-image0926.png"
                imageWidth={1732}
                imageHeight={908}
                twitterImage="/assets/twitter-og-image0926.png"
                twitterImageWidth={1605}
                twitterImageHeight={980}
            />
            {/* NOISE TEXTURE OVERLAY */}
            <div className="noise-overlay"></div>

            {/* ===== NAVIGATION ===== */}
            <div className="min-vh-100 d-flex flex-column">
                <CorporateHeader />



                {/* ===== HERO SECTION ===== */}
                <section className="hero-section pt-8half">
                    {/* LIVE TICKER */}
                    {fixturesLoading ? (
                        <TickerSkeleton />
                    ) : leadFixture && (
                        <div className='d-flex gap-3 mb-3half px-2 '>
                            <Link to={fixturesPreviewHref} className="text-decoration-none  hero-badge text-nowrap">
                                <span className="pulse-dot"></span>
                                {heroBadgeLabel} live
                            </Link>
                            <div className=" overflow-hidden d-flex align-items-center rounded-3" >
                                <LiveTicker fixtures={tickerFixtures} secondsPerFixture={18} />
                            </div>
                        </div>
                    )}


                    <div className="hero-bg-gradient-1"></div>
                    <div className="hero-bg-gradient-2"></div>
                    <div className="container-xl position-relative z-2 pb-7">
                        <div className="d-flex align-items-center justify-content-center hero-content-row gap-md-10half gap-7 mt-md-5">
                            {/* Left: Text */}
                            <div className="flex-grow-1 max-w-f-650 hero-copy-col">
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex flex-column gap-2">

                                        <h1 className="hero-heading hero-anim hero-anim-d2">
                                            <span className="gradient-text">Football Intelligence</span> <br />

                                            Multi-league predictions
                                        </h1>
                                        <p className="hero-sub hero-anim hero-anim-d3 mb-1">
                                            Verified news, sports AI and score predictions across the Premier League, La Liga and MLS.
                                        </p>
                                    </div>
                                    {/* Avatar group + Trustpilot */}
                                    {false && <div className="d-flex align-items-center gap-3 hero-anim hero-anim-d4">
                                        <div className="avatar-group">
                                            {avatars.map((avatar, idx) => (
                                                <img key={idx} src={avatar} alt="User" className="avatar" />
                                            ))}
                                        </div>
                                        <Link to={'https://uk.trustpilot.com/review/sportskinetic.ai'} target='_blank' className='text-decoration-none'>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="d-flex gap-1">
                                                    {[...Array(5)].map((_, i) => (
                                                        <span key={i} className="star-icon">&#9733;</span>
                                                    ))}
                                                </div>
                                                <span className='fw-semi-bold'>4.1</span>
                                            </div>
                                            <div className='extra-small mt-1'>Trustpilot</div>
                                        </Link>
                                    </div>}

                                    <div className='d-flex flex-column gap-2 hero-anim hero-anim-d3 mt-3 mb-5'>
                                        <div className='d-flex gap-2'>
                                            <Link to={fixturesPreviewHref} className="btn-sk-primary px-2 px-md-5 rounded-3 text-decoration-none  center-content"><span>See today&rsquo;s picks</span></Link>
                                            <Link to="/sign-up" className="btn bg-transparent px-2 px-md-5  btn-hover-transform border border-neutral-gray-900 center-content rounded-3 text-decoration-none  fw-normal"><span>Get started free</span></Link>
                                        </div>
                                        <div className='mt-1'>
                                            <CardInform />
                                        </div>
                                    </div>
                                    {(fixturesLoading || upcomingFixturePicks.length > 0) && (
                                        <>
                                            {fixturesLoading ? (
                                                <div className='mb-3 w-100 hero-anim hero-anim-d4'>
                                                    <CompetitionFilterSkeleton />
                                                </div>
                                            ) : competitions.length > 1 && (
                                                <div className='mb-3 w-100'>
                                                    <CompetitionFilter
                                                        competitions={competitions}
                                                        value={selectedCompetition}
                                                        onChange={setSelectedCompetition}
                                                        showAll={false}
                                                    />
                                                </div>
                                            )}
                                            {fixturesLoading ? (
                                                <div className='d-grid grid-md-2-cols grid-gap-2'>
                                                    {Array.from({ length: HERO_CARD_COUNT }).map((_, i) => (
                                                        <MarketPredictedCardSkeleton key={i} />
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className='d-grid grid-md-2-cols grid-gap-2'>
                                                    {upcomingFixturePicks.map((fixture) => (
                                                        <div key={fixture.fixture_id}>
                                                            <MarketPredictedCard fixture={fixture} />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                            </div>
                            {/* Right: Signup Card */}
                            <div className="signup-card hero-anim-slide align-self-center flex-shrink-0 py-6">


                                <TrialEmailForm onSuccess={() => navigate('/sign-up/add-password')} />

                            </div>
                        </div>
                    </div>


                </section>

                {newsFixture && (
                    <Suspense fallback={null}>
                        <section className="container-xl position-relative z-2  py-md-7 py-5">
                            {(newsFixtureDetail?.related_news?.length ?? 0) > 0 && (
                                <div className="mb-6">
                                    <RelatedIntelligence
                                        news={newsFixtureDetail.related_news.map((n) => ({
                                            text: n.headline,
                                            summary: n.summary,
                                            news_date: n.news_date,
                                            media: n.image,
                                            slug: n.slug,
                                            newsId: n.news_id,
                                            fixtureId: newsFixtureId,
                                            teams: [newsFixture.home_team, newsFixture.away_team],
                                            competition: { name: newsFixture.competition?.name },
                                        }))}
                                    />
                                </div>
                            )}
                            <PlayerNews heroFixture={newsFixture} publicMode limit={4} />

                            <div className="mt-6">
                                <MatchNews heroFixture={newsFixture} />
                            </div>
                        </section>
                    </Suspense>
                )}

                <Suspense fallback={null}>
                    <LandingBelowFold />
                </Suspense>

            </div>


        </div>
    );
};

export default LoggedOutHomePlusTwo;
